import React, { useState } from 'react';
import { useRecordContext, useUpdate, FieldProps } from 'react-admin';
import { Button, Modal, Paper, Chip, Box } from '@mui/material';
import { CremationStatus } from '../types';

const statusColors: Record<CremationStatus, string> = {
  [CremationStatus.ARRANGEMENT]: '#e0e0e0',
  [CremationStatus.TRANSPORT_DISPATCHED]: '#ffecb3',
  [CremationStatus.CREMATION_SCHEDULED]: '#bbdefb',
  [CremationStatus.READY_FOR_DELIVERY]: '#d1c4e9',
  [CremationStatus.DELIVERY_SCHEDULED]: '#ffcdd2',
  [CremationStatus.DELIVERED]: '#66bb6a'
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 1,
};

export const CremationStatusField: React.FC<FieldProps> = (props) => {
  const record = useRecordContext();
  const [update] = useUpdate();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<CremationStatus | null>(null);

  if (!record) return null;

  const currentStatus = (record.cremation_status as CremationStatus) || CremationStatus.ARRANGEMENT;
  const statusOrder = Object.values(CremationStatus);
  const currentIndex = statusOrder.indexOf(currentStatus);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
    setSelectedStatus(null);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setSelectedStatus(null);
  };

  const handleStatusSelect = (status: CremationStatus) => {
    setSelectedStatus(status);
  };

  const handleUpdate = async () => {
    if (!selectedStatus) return;
    
    try {
      // Prepare the update data with the new status
      const updateData: any = { 
        cremation_status: selectedStatus 
      };
      
      // Add additional fields based on the status
      // Normalize status for comparison (handle both 'transport_dispatched' and 'transport dispatched')
      const normalizedStatus = selectedStatus.toLowerCase().replace(/\s+/g, '_');
      if (normalizedStatus === CremationStatus.TRANSPORT_DISPATCHED.toLowerCase()) {
        updateData.transport_dispatched_date = new Date().toISOString();
      }
      
      await update(
        'funerals',
        {
          id: record.id,
          data: updateData,
          previousData: record
        },
        { mutationMode: 'pessimistic' }
      );
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while updating the status');
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Chip
        label={currentStatus}
        // onClick={handleClick}
        sx={{
          backgroundColor: statusColors[currentStatus],
          // cursor: 'pointer',
          // '&:hover': {
          //   opacity: 0.8
          // }
        }}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="cremation-status-modal"
      >
        <Paper sx={modalStyle}>
          <h2 id="cremation-status-modal" style={{ margin: '0 0 20px 0' }}>
            Update Cremation Status
          </h2>
          
          {error ? (
            <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>
          ) : (
            <div style={{ margin: '20px 0' }}>
              {statusOrder.map((status, index) => {
                const isPastStatus = index < currentIndex;
                const isCurrent = status === currentStatus;
                const isSelected = status === selectedStatus;

                return (
                  <Box
                    key={status}
                    onClick={() => handleStatusSelect(status)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      cursor: 'pointer',
                      opacity: isPastStatus ? 0.7 : 1,
                      position: 'relative',
                      marginBottom: '12px',
                      backgroundColor: isCurrent ? '#e3f2fd' : isSelected ? '#f3e5f5' : 'transparent',
                      borderRadius: '4px',
                      transition: 'all 0.2s',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: isPastStatus ? '#fff3e0' : isSelected ? '#f3e5f5' : '#f5f5f5'
                      }
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: isCurrent ? '#1976d2' : isSelected ? '#9c27b0' : isPastStatus ? 'gray' : '#bdbdbd',
                        marginRight: '12px',
                        flexShrink: 0
                      }}
                    />
                    {index < statusOrder.length - 1 && (
                      <div
                        style={{
                          position: 'absolute',
                          left: '7px',
                          top: '28px',
                          height: '24px',
                          width: '2px',
                          backgroundColor: '#e0e0e0'
                        }}
                      />
                    )}
                    <span>
                      {status}
                      {isCurrent && ' (Current)'}
                      {isSelected && ' (Selected)'}
                      {isPastStatus && !isCurrent && !isSelected}
                    </span>
                  </Box>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              variant="contained"
              disabled={!selectedStatus || selectedStatus === currentStatus}
              color={selectedStatus && statusOrder.indexOf(selectedStatus) < currentIndex ? 'warning' : 'primary'}
            >
              {selectedStatus ? (
                statusOrder.indexOf(selectedStatus) < currentIndex 
                  ? `Revert to ${selectedStatus}`
                  : `Update to ${selectedStatus}`
              ) : 'Select a status'}
            </Button>
          </div>
        </Paper>
      </Modal>
    </div>
  );
}; 